/**
 * Tour Definitions - Guided tour configurations for different views
 * Each tour is a sequence of steps with element targeting, highlights, and tooltips
 */

export const tourDefinitions = [
  {
    id: 'admin-projects-intro',
    title: 'Admin Projects Workflow',
    description: 'Get oriented to project operations in the Admin Projects workspace',
    estimatedTime: '3-5 minutes',
    steps: [
      {
        id: 'admin-intro',
        title: 'Welcome to Admin Projects',
        content: 'Use this workspace to manage Firestore projects, connect API projects, and organize spreadsheet links in one operational flow.',
        element: null, // Overlay mode for intro
        position: 'center',
        action: 'highlight',
      },
      {
        id: 'admin-tabs',
        title: 'Project Management Tabs',
        content: 'Each tab maps to a core function: projects, API sync, spreadsheet linking, project types, statuses, and platforms.',
        element: '[role="presentation"] [role="tab"]', // v-tabs selector
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'admin-projects-tab',
        title: 'Projects Tab Overview',
        content: 'The Projects tab lists Firestore records and supports create, edit, delete, and linking actions.',
        element: '[role="tab"][aria-selected="true"]',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'admin-count-badges',
        title: 'Tab Count Badges',
        content: 'Count badges show current volume per tab so you can assess workload at a glance.',
        element: '.v-tab .v-chip',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'admin-export',
        title: 'Export & Share Data',
        content: 'Use Export to download project data as CSV or XLSX for reporting, audits, and handoffs.',
        element: '[aria-label*="xport"], .v-menu',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'admin-help',
        title: 'Help & Documentation',
        content: 'Open Help with ? or visit /docs/guides#admin for detailed admin workflows and references.',
        element: null,
        position: 'center',
        action: 'info',
      },
    ],
  },

  {
    id: 'time-entry-quick-start',
    title: 'Time Entry Quick Start',
    description: 'Review the essentials for entering, organizing, and validating time',
    estimatedTime: '2-3 minutes',
    steps: [
      {
        id: 'time-intro',
        title: 'Welcome to Time Tracking',
        content: 'Use the Time view to log hours, organize entries by project and client, and support billing outputs.',
        element: null,
        position: 'center',
        action: 'highlight',
      },
      {
        id: 'time-tabs',
        title: 'Time View Tabs',
        content: 'Tabs segment your workflow: Time Entry for daily logging, MTD for monthly summary, Billable for client billing, and Projects for rollup views.',
        element: '[role="tab"]',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'time-entry-form',
        title: 'Creating a New Entry',
        content: 'Select New (or press N) to add a time entry, then set date, classification, hours, and optional client/project fields.',
        element: 'button:has-text("New"), button:has-text("+")',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'time-calendar',
        title: 'Calendar View',
        content: 'Switch to Calendar layout for a weekly visual view and use drag-and-drop to move entries across days.',
        element: '[aria-label*="ayout"], .v-btn:has-text("Calendar")',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'time-help',
        title: 'Learn More',
        content: 'Open Help with ? or visit /docs/guides#time for full workflows, policy notes, and best practices.',
        element: null,
        position: 'center',
        action: 'info',
      },
    ],
  },

  {
    id: 'projects-dashboard-overview',
    title: 'Projects Dashboard Tour',
    description: 'Learn the key controls for managing project portfolios and status',
    estimatedTime: '2-3 minutes',
    steps: [
      {
        id: 'projects-intro',
        title: 'Projects Dashboard',
        content: 'Manage your full project portfolio here: create records, update details, and monitor status and delivery timelines.',
        element: null,
        position: 'center',
        action: 'highlight',
      },
      {
        id: 'projects-tabs',
        title: 'Status Tabs',
        content: 'Tabs organize projects by lifecycle state: Active, Archived, and Deleted, so you can focus on the right operating set.',
        element: '[role="tab"]',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'projects-search',
        title: 'Search Projects',
        content: 'Use Search in the toolbar to filter by project name, code, ID, or status with live result updates.',
        element: '.controls-bar input[type="text"]',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'projects-filter',
        title: 'Advanced Filters',
        content: 'Use Filter to narrow results by billing code, type, platform, and other operational criteria.',
        element: '.tools-toolbar .toolbar-btn',
        position: 'bottom',
        action: 'highlight',
      },
      {
        id: 'projects-card',
        title: 'Project Card',
        content: 'Each project card surfaces key metadata such as name, status, owner, type, and linked API/spreadsheet references.',
        element: '.project-card, .v-card',
        position: 'right',
        action: 'highlight',
      },
      {
        id: 'projects-help',
        title: 'Need Help?',
        content: 'Open Help with ? or visit /docs/guides#projects for detailed operating workflows and advanced feature guidance.',
        element: null,
        position: 'center',
        action: 'info',
      },
    ],
  },
]

/**
 * Get a tour by ID
 */
export function getTourById(tourId) {
  return tourDefinitions.find((t) => t.id === tourId)
}

/**
 * Get all available tours
 */
export function getAvailableTours() {
  return tourDefinitions
}

/**
 * Filter tours by target view
 */
export function getToursByView(viewName) {
  const viewMap = {
    admin: 'admin-projects-intro',
    time: 'time-entry-quick-start',
    projects: 'projects-dashboard-overview',
  }
  const tourId = viewMap[viewName]
  return tourId ? [getTourById(tourId)] : []
}
